import React, {useLayoutEffect, useState} from 'react';
import axios from "axios";
import {
    Button,
    Card,
    CardBody,
    Dialog,
    DialogBody, DialogFooter,
    DialogHeader,
    Input,
    Typography
} from "@material-tailwind/react";
import MapComponent from "../../shared/map_component";
import {NumericFormat} from "react-number-format";

const LandCorrection = ({lot, movement}) => {
    const [error, setError] = useState(null);
    const [loaderActive, setLoaderActive] = useState(false);
    const [openEndDialog, setOpenEndDialog] = useState(false);
    const [currentLot, setCurrentLot] = useState([]);
    const [assignedLands, setAssignedLands] = useState({});

    const getCSRFToken = () => {
        const token = document.querySelector('meta[name="csrf-token"]');
        return token ? token.content : "";
    };

    const handleReset = () => {
        window.location.replace('movements');
    };

    useLayoutEffect(() => {
        setCurrentLot(lot);

        async function fetchData() {
            try {
                const [landResponse] = await Promise.all([
                    fetch(`/lots/${lot.parcel_code}/land.json`),
                ]);

                if (landResponse.ok) {
                    const landData = await landResponse.json();

                    const initAssignments = {};
                    landData.forEach((land) => {
                        initAssignments[land.id] = {
                            area: land.area,
                            current_area: land.area,
                            land_type_id: land.location_type_id,
                            rustic_land_value_id: land.rustic_land_value_id,
                            new_land: false,
                        };
                    });
                    setAssignedLands(initAssignments);
                } else {
                    console.log("No se encontraron terrenos para este código.");
                }
            } catch (err) {
                console.error("Error al buscar el predio:", err);
            }
        }

        fetchData();
    }, [lot]);

    const handleLandChange = (id, value) => {
        setAssignedLands((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                area: value,
            },
        }));
    };

    const handleSubmit = async () => {
        setLoaderActive(true);

        const updatedLands = Object.entries(assignedLands)
            .filter(([_, data]) =>
                data.area >= 0 &&
                !data.new_land
            )
            .map(([id, data]) => ({
                id: Number(id),
                area: data.area,
            }));

        const newLands = Object.entries(assignedLands)
            .filter(([_, data]) => data.new_land)
            .map(([id, data]) => ({
                area: data.area,
                land_type_id: data.land_type_id,
                rustic_land_value_id: data.rustic_land_value_id,
            }));

        try {
            const response = await axios.post(`lots/${lot.parcel_code}/revaluation.json`, {
                lot: {
                    notes: currentLot.notes,
                    reason_type_id: movement.id,
                    effective_bimester: currentLot.effective_bimester,
                    effective_year: currentLot.effective_year,
                },
                new_lands: newLands,
                updated_lands: updatedLands,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    "X-CSRF-Token": getCSRFToken(),
                }
            });
            if (response.status === 200) {
                setOpenEndDialog(true);
                const current_lot_url = `/lots/${lot.parcel_code}/print_movement.pdf`;
                window.open(current_lot_url, '_blank');
                setLoaderActive(false);
            } else {
                setError('Error al actualizar el predio. Verifica los datos.');
                setLoaderActive(false);
            }
        } catch (err) {
            setError('Error al guardar. Verifica los datos.');
            setLoaderActive(false);
        }
    };

    const totalArea = assignedLands ? Object.values(assignedLands).reduce((acc, land) => acc + Number(land.area || 0), 0) : 0;
    const getUnit = lot.lot_location_type === 'URBANO' ? <>m<sup>2</sup></> : <>ha</>;

    return (
        <>
            <div className="flex-auto py-6 pt-0">
                <h6 className="font-semibold uppercase bg-blue-500 text-white p-2 rounded-t-lg">
                    Información del predio actual
                </h6>
                <div className="flex flex-col lg:flex-row gap-4 min-h-[350px]">
                    <div
                        className="flex-grow border border-blue-gray-100 p-4 rounded-t-none rounded-b-lg w-full lg:w-1/2 overflow-x-hidden">
                        <CardBody className='p-0'>
                            <div className="w-full mb-4 bg-gray-100">
                                {(lot.latitude && lot.longitude) && (
                                    <MapComponent
                                        parcel_code={lot.parcel_code}
                                        latitude={lot.latitude}
                                        longitude={lot.longitude}
                                    />
                                )}
                            </div>
                            <div className='flex flex-col md:flex-row gap-4 w-full text-center'>
                                <div className='flex flex-col w-full'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Dirección
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        {lot.road_name}{lot.ext_num ? " #" + lot.ext_num : ""}, {lot.neighborhood_name},
                                        C.P. {lot.postal_code}
                                    </Typography>
                                </div>
                                <div className='flex flex-col w-full'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Referencias
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        {lot.address_references === null ? '' : lot.address_references}
                                    </Typography>
                                </div>
                            </div>
                            <hr className="my-6 border-b-1 border-blue-gray-100"/>
                            <h6 className="text-blue-gray-600 text-sm mt-3 mb-2 font-bold uppercase">
                                REGISTRO DEL PREDIO
                            </h6>
                            <div className="flex flex-wrap">
                                <div className="w-full lg:w-12/12 px-0">
                                    <div className="relative w-full mb-3">
                                        <div className='py-2'>
                                            <Typography
                                                variant="small"
                                                className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                                            >
                                                R.P.P.
                                            </Typography>
                                            <div className='flex flex-row gap-4 w-full text-center'>
                                                <div className='flex flex-col w-full'>
                                                    <Typography
                                                        variant="small"
                                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                    >
                                                        Insc.
                                                    </Typography>
                                                    <Typography
                                                        variant="paragraph"
                                                        className="font-medium"
                                                    >
                                                        {lot.inscription}
                                                    </Typography>
                                                </div>
                                                <div className='flex flex-col w-full'>
                                                    <Typography
                                                        variant="small"
                                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                    >
                                                        Folio
                                                    </Typography>
                                                    <Typography
                                                        variant="paragraph"
                                                        className="font-medium"
                                                    >
                                                        {lot.folio}
                                                    </Typography>
                                                </div>
                                                <div className='flex flex-col w-full'>
                                                    <Typography
                                                        variant="small"
                                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                    >
                                                        Libro
                                                    </Typography>
                                                    <Typography
                                                        variant="paragraph"
                                                        className="font-medium"
                                                    >
                                                        {lot.book}
                                                    </Typography>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='py-2'>
                                            <Typography
                                                variant="small"
                                                className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                                            >
                                                Fecha efecto
                                            </Typography>
                                            <div className='flex flex-row gap-4 w-full text-center'>
                                                <div className='flex flex-col w-full'>
                                                    <Input value={currentLot.effective_bimester}
                                                           onChange={(e) => setCurrentLot({
                                                               ...currentLot,
                                                               effective_bimester: e.target.value,
                                                           })}
                                                           label={'Bim'}
                                                           containerProps={{
                                                               className: '!min-w-0'
                                                           }}
                                                    />
                                                </div>
                                                <div className='flex flex-col w-full'>
                                                    <Input value={currentLot.effective_year}
                                                           onChange={(e) => setCurrentLot({
                                                               ...currentLot,
                                                               effective_year: e.target.value,
                                                           })}
                                                           label={'Año'}
                                                           containerProps={{
                                                               className: '!min-w-0'
                                                           }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='py-2'>
                                            <Typography
                                                variant="small"
                                                className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                                            >
                                                Fecha exento
                                            </Typography>
                                            <div className='flex flex-row gap-4 w-full text-center'>
                                                <div className='flex w-full gap-1'>
                                                    <div className='flex flex-col w-full'>
                                                        <Typography
                                                            variant="small"
                                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                        >
                                                            Bim. inicio
                                                        </Typography>
                                                        <Typography
                                                            variant="paragraph"
                                                            className="font-medium"
                                                        >
                                                            {lot.start_exempt_bimester}
                                                        </Typography>
                                                    </div>
                                                    <div className='flex flex-col w-full'>
                                                        <Typography
                                                            variant="small"
                                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                        >
                                                            Año inicio
                                                        </Typography>
                                                        <Typography
                                                            variant="paragraph"
                                                            className="font-medium"
                                                        >
                                                            {lot.start_exempt_year}
                                                        </Typography>
                                                    </div>
                                                </div>
                                                <div className='flex w-full gap-1'>
                                                    <div className='flex flex-col w-full'>
                                                        <Typography
                                                            variant="small"
                                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                        >
                                                            Bim. fin
                                                        </Typography>
                                                        <Typography
                                                            variant="paragraph"
                                                            className="font-medium"
                                                        >
                                                            {lot.end_exempt_bimester}
                                                        </Typography>
                                                    </div>
                                                    <div className='flex flex-col w-full'>
                                                        <Typography
                                                            variant="small"
                                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                        >
                                                            Año fin
                                                        </Typography>
                                                        <Typography
                                                            variant="paragraph"
                                                            className="font-medium"
                                                        >
                                                            {lot.end_exempt_year}
                                                        </Typography>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <hr className="my-6 border-b-1 border-blue-gray-100"/>
                            <h6 className="text-blue-gray-600 text-sm mt-3 mb-2 font-bold uppercase">
                                PROPIETARIO
                            </h6>
                            <div className="flex flex-wrap">
                                <div className="w-full lg:w-12/12 px-0">
                                    <div className="relative w-full mb-3">
                                        <div className='py-2'>
                                            {lot.contributor_type === 'P' ? (
                                                <div className='flex flex-row gap-4 w-full text-center'>
                                                    <div className='flex flex-col w-full'>
                                                        <Typography
                                                            variant="small"
                                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                        >
                                                            Nombre
                                                        </Typography>
                                                        <Typography
                                                            variant="paragraph"
                                                            className="font-medium"
                                                        >
                                                            {lot.name}
                                                        </Typography>
                                                    </div>
                                                    <div className='flex flex-col w-full'>
                                                        <Typography
                                                            variant="small"
                                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                        >
                                                            Apellido paterno
                                                        </Typography>
                                                        <Typography
                                                            variant="paragraph"
                                                            className="font-medium"
                                                        >
                                                            {lot.parental_last_name}
                                                        </Typography>
                                                    </div>
                                                    <div className='flex flex-col w-full'>
                                                        <Typography
                                                            variant="small"
                                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                        >
                                                            Apellido Materno
                                                        </Typography>
                                                        <Typography
                                                            variant="paragraph"
                                                            className="font-medium"
                                                        >
                                                            {lot.maternal_last_name}
                                                        </Typography>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className='flex flex-row gap-4 w-full text-center'>
                                                    <div className='flex flex-col w-full'>
                                                        <Typography
                                                            variant="small"
                                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                        >
                                                            Razón social
                                                        </Typography>
                                                        <Typography
                                                            variant="paragraph"
                                                            className="font-medium"
                                                        >
                                                            {lot.name}
                                                        </Typography>
                                                    </div>
                                                </div>
                                            )}
                                            <div className='flex flex-row gap-4 mt-4 w-full text-center'>
                                                <div className='flex flex-col w-full'>
                                                    <Typography
                                                        variant="small"
                                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                    >
                                                        Dirección
                                                    </Typography>
                                                    <Typography
                                                        variant="paragraph"
                                                        className="font-medium"
                                                    >
                                                        {lot.contributor_street}
                                                        {lot.contributor_ext_num ? " #" + lot.contributor_ext_num : ""}
                                                        {lot.contributor_neighborhood ? ', ' + lot.contributor_neighborhood : ''}
                                                        {lot.contributor_postal_code ? ', C.P. ' + lot.contributor_postal_code : ''}
                                                    </Typography>
                                                </div>
                                                <div className='flex flex-col w-full'>
                                                    <Typography
                                                        variant="small"
                                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                    >
                                                        Teléfono
                                                    </Typography>
                                                    <Typography
                                                        variant="paragraph"
                                                        className="font-medium"
                                                    >
                                                        {lot.phone ? lot.phone : 'N/A'}
                                                    </Typography>
                                                </div>
                                                <div className='flex flex-col w-full'>
                                                    <Typography
                                                        variant="small"
                                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                    >
                                                        RFC
                                                    </Typography>
                                                    <Typography
                                                        variant="paragraph"
                                                        className="font-medium"
                                                    >
                                                        {lot.rfc ? lot.rfc : 'N/A'}
                                                    </Typography>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <hr className="my-6 border-b-1 border-blue-gray-100"/>
                            <h6 className="text-blue-gray-600 text-sm mt-3 mb-2 font-bold uppercase">
                                DIVISIÓN DE TERRENO
                            </h6>
                            <div className="flex flex-wrap">
                                <div className="w-full lg:w-12/12 px-0">
                                    <div className="relative w-full mb-3">
                                        <Card className='w-full border-0' shadow={false}>
                                            <CardBody className='p-0'>
                                                <table className="w-full min-w-[640px] table-auto border">
                                                    <thead>
                                                    <tr className='bg-gray-50'>
                                                        {["Cve. Val.", "Descripción", "Área"].map(
                                                            (el) => (
                                                                <th
                                                                    key={el}
                                                                    className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                                                >
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                                    >
                                                                        {el}
                                                                    </Typography>
                                                                </th>
                                                            )
                                                        )}
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {(!Object.entries(assignedLands) || Object.entries(assignedLands).length === 0) ? (
                                                        <tr className="w-full">
                                                            <td colSpan="100%" className="text-center py-3">
                                                                <strong>El predio no cuenta con terrenos
                                                                    registrados.</strong>
                                                            </td>
                                                        </tr>
                                                    ) : (Object.entries(assignedLands).map(([landId, data], index, array) => {
                                                            const className = `py-3 px-5 ${
                                                                index === array.length - 1 ? "" : "border-b border-blue-gray-50"
                                                            }`;
                                                            return (
                                                                <tr key={landId}
                                                                    className="hover:bg-gray-50 cursor-pointer">
                                                                    <td className={className}>{data.rustic_land_value_id ? data.rustic_land_value_id : 'N/A'}</td>
                                                                    <td className={className}>{data.rustic_land_desc ? data.rustic_land_desc : 'Terreno urbano'}</td>
                                                                    <td className={className}>
                                                                        <Input
                                                                            value={data.area || 0.0}
                                                                            onChange={(e) => handleLandChange(landId, e.target.value)}
                                                                            containerProps={{className: '!min-w-0'}}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                    </tbody>
                                                    <tfoot>
                                                    <tr className='border-t'>
                                                        <td colSpan='2'
                                                            className='py-3 px-5 text-right border-r'>
                                                            <Typography
                                                                variant="small"
                                                                className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                            >
                                                                Totales
                                                            </Typography>
                                                        </td>
                                                        <td className='py-3 px-5 cursor-pointer bg-gray-50 border-b'>
                                                            <NumericFormat
                                                                value={totalArea.toFixed(4)}
                                                                displayType={'text'}
                                                                allowLeadingZeros
                                                                thousandSeparator=","
                                                            /> {getUnit}
                                                        </td>
                                                    </tr>
                                                    </tfoot>
                                                </table>
                                            </CardBody>
                                        </Card>
                                    </div>
                                </div>
                            </div>
                            <hr className="my-6 border-b-1 border-blue-gray-100"/>
                            <h6 className="text-blue-gray-600 text-sm mt-3 mb-2 font-bold uppercase">
                                OBSERVACIONES
                            </h6>
                            <div className="flex flex-wrap">
                                <div className="w-full lg:w-12/12 px-0">
                                    <div className='flex flex-row gap-4'>
                                        <div className="relative w-full mb-3">
                                            <Typography
                                                variant="small"
                                                className="text-[11px] font-medium uppercase text-blue-gray-400"
                                            >
                                                OBSERVACIONES MOVIMIENTO DE PREDIO
                                            </Typography>
                                            <textarea
                                                type="text"
                                                className="border-blue-gray-100 px-3 py-3 placeholder-blue-gray-300 text-blue-gray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                                value={currentLot.notes}
                                                onChange={(e) => setCurrentLot({
                                                    ...currentLot,
                                                    notes: e.target.value,
                                                })}
                                                rows="6"
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardBody>
                        <Button
                            className="w-full bg-green-500 mt-4 hover:bg-green-600 text-white rounded-md px-5 py-3 font-semibold"
                            onClick={() => handleSubmit()}
                            disabled={loaderActive}
                        >
                            GUARDAR
                        </Button>
                        {
                            error && <p style={{color: "red"}}>{error}</p>
                        }
                    </div>
                </div>
            </div>
            <Dialog open={openEndDialog} handler={setOpenEndDialog}>
                <DialogHeader>Guardado</DialogHeader>
                <DialogBody>
                    <Typography>
                        Se actualizo el predio
                    </Typography>
                </DialogBody>
                <DialogFooter>
                    <Button
                        className="bg-green-500 text-white"
                        onClick={() => handleReset()}
                        disabled={loaderActive}
                    >
                        Finalizar
                    </Button>
                </DialogFooter>
            </Dialog>
        </>
    )
};

export default LandCorrection;
